import { Bell, Star, Heart, Users, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { fr, es, enUS } from "date-fns/locale";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useNotifications, type Notification } from "@/hooks/useNotifications";
import { useLanguage } from "@/hooks/useLanguage";
import { cn } from "@/lib/utils";

const getDateLocale = (lang: string) => {
  switch (lang) {
    case 'fr': return fr;
    case 'es': return es;
    default: return enUS;
  }
};

const NotificationIcon = ({ type }: { type: Notification['type'] }) => {
  switch (type) {
    case 'new_review':
      return <Star className="h-4 w-4 text-yellow-500 shrink-0" />;
    case 'new_like':
      return <Heart className="h-4 w-4 text-red-500 shrink-0" />;
    case 'new_collaborator':
      return <Users className="h-4 w-4 text-blue-500 shrink-0" />;
  }
};

const NotificationItem = ({
  notification,
  onClickNotification,
}: {
  notification: Notification;
  onClickNotification: (n: Notification) => void;
}) => {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();

  const getMessage = () => {
    const actor = notification.actor_name || t('notifications.someone');
    const trip = notification.trip_title || t('notifications.aTrip');

    switch (notification.type) {
      case 'new_review': {
        const rating = notification.data?.rating;
        return t('notifications.newReview', { actor, trip, rating: rating || '' });
      }
      case 'new_like':
        return t('notifications.newLike', { actor, trip });
      case 'new_collaborator':
        return t('notifications.newCollaborator', { actor, trip });
    }
  };

  const timeAgo = formatDistanceToNow(new Date(notification.created_at), {
    addSuffix: true,
    locale: getDateLocale(currentLanguage),
  });

  return (
    <button
      onClick={() => onClickNotification(notification)}
      className={cn(
        "w-full text-left px-4 py-3 hover:bg-muted/50 transition-colors border-b border-border/50 last:border-0",
        !notification.is_read && "bg-primary/5"
      )}
    >
      <div className="flex gap-3 items-start">
        <div className="mt-0.5">
          <NotificationIcon type={notification.type} />
        </div>
        <div className="flex-1 min-w-0">
          <p className={cn("text-sm leading-snug", !notification.is_read && "font-medium")}>
            {getMessage()}
          </p>
          <p className="text-xs text-muted-foreground mt-1">{timeAgo}</p>
        </div>
        {!notification.is_read && (
          <div className="w-2 h-2 rounded-full bg-primary shrink-0 mt-1.5" />
        )}
      </div>
    </button>
  );
};

const NotificationBell = () => {
  const { t } = useTranslation();
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const { getLocalizedPath } = useLanguage();
  const navigate = useNavigate();

  const handleClickNotification = (notification: Notification) => {
    if (!notification.is_read) {
      markAsRead(notification.id);
    }
    if (notification.trip_slug || notification.trip_id) {
      navigate(getLocalizedPath(`/trip/${notification.trip_slug || notification.trip_id}`));
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative h-9 w-9">
          <Bell className="h-4 w-4" />
          <AnimatePresence>
            {unreadCount > 0 && (
              <motion.span
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0 }}
                className="absolute -top-0.5 -right-0.5 flex items-center justify-center h-4 min-w-4 px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold"
              >
                {unreadCount > 9 ? '9+' : unreadCount}
              </motion.span>
            )}
          </AnimatePresence>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" sideOffset={8}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h3 className="font-semibold text-sm">{t('notifications.title')}</h3>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs gap-1"
              onClick={markAllAsRead}
            >
              <Check className="h-3 w-3" />
              {t('notifications.markAllRead')}
            </Button>
          )}
        </div>
        <ScrollArea className="max-h-80">
          {notifications.length === 0 ? (
            <div className="py-6 px-4 text-center">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                <Bell className="h-6 w-6 text-muted-foreground" />
              </div>
              <p className="text-sm font-medium mb-1">{t('notifications.empty')}</p>
              <p className="text-xs text-muted-foreground mb-3">{t('notifications.emptyDescription')}</p>
              <div className="space-y-1.5 text-left">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Star className="h-3.5 w-3.5 text-yellow-500 shrink-0" />
                  <span>{t('notifications.typeReview')}</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Heart className="h-3.5 w-3.5 text-red-500 shrink-0" />
                  <span>{t('notifications.typeLike')}</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Users className="h-3.5 w-3.5 text-blue-500 shrink-0" />
                  <span>{t('notifications.typeCollaborator')}</span>
                </div>
              </div>
            </div>
          ) : (
            notifications.map(notification => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onClickNotification={handleClickNotification}
              />
            ))
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
